import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Calendar, CalendarType } from '@demind-inc/core';
import { groupBy, upperFirst } from 'lodash';
import { Error, MoreVert, ArrowDropDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  CircularProgress,
  IconButton,
} from '@mui/material';

import './CalendarsList.scss';
import { ManageCalendarsBtn } from './ManageCalendarsBtn';
import { CalendarListItem } from './CalendarListItem';
import { CustomTooltip } from '../../common';
import { SyncErrorTooltipContent } from './SyncErrorTooltipContent';
import {
  CalendarListMoreDropdown,
  CalendarListMoreDropdownOption,
} from './CalendarListMoreDropdown';
import { useDropdownState, useSyncCalendarLists } from '../../../hooks';
import { useAuthContext, useDeleteCalendarsInfo } from '../../../data-access';
import { isAppleCalendarDesktopSupported } from '../../../helpers';

import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar-icon.svg';
import GoogleIcon from '../../../assets/calendar/google.png';
import OutlookIcon from '../../../assets/calendar/outlook.png';
import AppleIcon from '../../../assets/calendar/apple.png';
import { useTranslation } from 'react-i18next';

type CalendarProviderType = Extract<Calendar['calendarType'], 'google' | 'outlook' | 'apple'>;

const providerCalendarTypes: CalendarProviderType[] = ['google', 'outlook', 'apple'];

const isProviderCalendarType = (
  calendarType?: Calendar['calendarType']
): calendarType is CalendarProviderType => {
  if (!calendarType) {
    return false;
  }

  return providerCalendarTypes.includes(calendarType as CalendarProviderType);
};

interface CalendarAvatarData {
  src?: string | React.ReactNode;
  alt?: string;
  content: React.ReactNode;
}

const getCalendarAvatarData = (
  calendarType: Calendar['calendarType'] | undefined,
  rootEmail: string
): CalendarAvatarData => {
  const fallbackInitial = upperFirst(rootEmail?.charAt(0) ?? '');

  switch (calendarType) {
    case 'lifestack':
      return {
        src: <CalendarIcon />,
        alt: 'Lifestack Calendar',
        content: null,
      };
    case 'google':
      return {
        src: GoogleIcon,
        alt: 'Google Calendar',
        content: null,
      };
    case 'outlook':
      return {
        src: OutlookIcon,
        alt: 'Outlook Calendar',
        content: null,
      };
    case 'apple':
      return {
        src: AppleIcon,
        alt: 'Apple Calendar',
        content: null,
      };
    default:
      return {
        content: fallbackInitial,
      };
  }
};

interface CalendarsListProps {
  calendarsMeta: Calendar[];
  visibleCalendarIds: string[];
  isFetchingCalendars: boolean;
  syncErrorRootEmail?: string;
  onSelectCalendar?: (calendar: Calendar, checked: boolean) => void;
  onConnectCalendar: (calendarType: CalendarType) => Promise<boolean> | boolean | void;
  onDisconnectCalendar: (calendarIds: string[]) => void;
  isDisconnectingCalendars: boolean;
  isConnectingCalendars: boolean;
  className?: string;
  defaultExpanded?: boolean;
}

interface TargetCalendarForDropdown {
  type: CalendarType;
  rootEmail: string;
  calendarIds: string[];
}

const CalendarsList: React.FC<CalendarsListProps> = ({
  calendarsMeta,
  visibleCalendarIds,
  isFetchingCalendars,
  syncErrorRootEmail,
  onSelectCalendar = () => void 0,
  onConnectCalendar,
  onDisconnectCalendar,
  isDisconnectingCalendars,
  isConnectingCalendars,
  className,
  defaultExpanded = true,
}) => {
  const { t } = useTranslation();
  const calendarsGroupedByType = useMemo(() => {
    const groupedByType = groupBy(calendarsMeta, 'calendarType');
    return Object.entries(groupedByType).reduce(
      (acc, [type, calendars]) => {
        acc[type] = groupBy(calendars, 'rootEmail');
        return acc;
      },
      {} as Record<CalendarType, Record<string, Calendar[]>>
    );
  }, [calendarsMeta]);

  const { anchorEl, handleCloseDropdown, handleOpenDropdown } = useDropdownState();
  const { syncCalendarsList, isSyncing } = useSyncCalendarLists();
  const [targetCalendarForDropdown, setTargetCalendarForDropdown] =
    useState<TargetCalendarForDropdown | null>(null);
  const { deleteCalendarsInfo, isPending: isDeleting } = useDeleteCalendarsInfo();
  const { user } = useAuthContext();
  const isAppleCalendarSupported = isAppleCalendarDesktopSupported();

  const disabledCalendarType: CalendarType[] = useMemo(() => {
    const hasOutlookCalendar = calendarsMeta.some(({ calendarType }) => calendarType === 'outlook');
    return hasOutlookCalendar ? ['outlook'] : []; //#469 Support multipe outlook calendar
  }, [calendarsMeta.length]);

  const syncErrorCalendarType = useMemo(() => {
    return calendarsMeta.find(({ rootEmail }) => rootEmail === syncErrorRootEmail)?.calendarType;
  }, [syncErrorRootEmail, calendarsMeta]);

  const loadingOption: CalendarListMoreDropdownOption = useMemo(() => {
    if (isSyncing) {
      return 'sync';
    }
    if (isDeleting) {
      return 'delete';
    }
    return;
  }, [isSyncing, isDeleting]);

  const canDisconnectCalendarForDropdown = useMemo(() => {
    if (!targetCalendarForDropdown) {
      return false;
    }

    if (targetCalendarForDropdown.type === 'apple') {
      return isAppleCalendarSupported;
    }

    return targetCalendarForDropdown.rootEmail !== user.email;
  }, [targetCalendarForDropdown, user.email, isAppleCalendarSupported]);

  const handleMoreDropdownItemClick = async (option: CalendarListMoreDropdownOption) => {
    if (!targetCalendarForDropdown) {
      handleCloseCalendarDropdown();
      return;
    }

    if (targetCalendarForDropdown.type === 'apple' && !isAppleCalendarSupported) {
      handleCloseCalendarDropdown();
      return;
    }

    try {
      if (option === 'sync' && targetCalendarForDropdown.type !== 'lifestack') {
        await syncCalendarsList({
          calendarType: targetCalendarForDropdown.type,
          rootEmail: targetCalendarForDropdown.rootEmail,
        });
      } else if (option === 'delete' && targetCalendarForDropdown.type !== 'lifestack') {
        await deleteCalendarsInfo({
          userId: user.userId,
          targetCalendarIds: targetCalendarForDropdown.calendarIds,
        });
      }
    } catch (error) {
      console.error('Failed to execute calendar dropdown action:', error);
    } finally {
      handleCloseCalendarDropdown();
    }
  };

  const handleCloseCalendarDropdown = () => {
    handleCloseDropdown();
    setTargetCalendarForDropdown(null);
  };
  return (
    <div className={clsx('calendars-list', className)}>
      <div className="calendars-list__content">
        {isFetchingCalendars && <CircularProgress size={20} className="calendars-list__loading" />}
        {!isFetchingCalendars &&
          Object.entries(calendarsGroupedByType).map(([calendarType, rootEmailGroups]) => (
            <div key={calendarType} className="calendars-list__type-group">
              {Object.entries(rootEmailGroups).map(([rootEmail, calendars]) => {
                const normalizedCalendarType = calendarType as Calendar['calendarType'] | undefined;
                const calendarAvatar = getCalendarAvatarData(normalizedCalendarType, rootEmail);
                const isProviderCalendar = isProviderCalendarType(normalizedCalendarType);
                const hasError =
                  syncErrorRootEmail === rootEmail &&
                  calendars.some(({ calendarType }) => calendarType !== 'lifestack');
                return (
                  <Accordion
                    key={rootEmail}
                    className="calendars-list__accordion"
                    elevation={0}
                    defaultExpanded={defaultExpanded}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ArrowDropDown className="calendars-list__accordion__title__more" />
                      }
                    >
                      <div className="calendars-list__accordion__title">
                        {typeof calendarAvatar.src === 'string' ? (
                          <Avatar
                            className={clsx('calendars-list__accordion__title__icon', {
                              'calendars-list__accordion__title__icon--provider':
                                isProviderCalendar,
                              'calendars-list__accordion__title__icon--logo':
                                normalizedCalendarType === 'lifestack',
                            })}
                            src={calendarAvatar.src}
                            alt={calendarAvatar.alt}
                          />
                        ) : (
                          <div
                            className={clsx(
                              'calendars-list__accordion__title__icon',
                              'calendars-list__accordion__title__icon--lifestack'
                            )}
                          >
                            {calendarAvatar.src}
                          </div>
                        )}
                        <span className="calendars-list__accordion__title__email">
                          {normalizedCalendarType === 'lifestack' ? 'Lifestack' : rootEmail}
                        </span>
                        {!hasError &&
                          normalizedCalendarType &&
                          normalizedCalendarType !== 'lifestack' &&
                          (normalizedCalendarType !== 'apple' || isAppleCalendarSupported) && (
                            <IconButton
                              aria-label={t('calendar.sidebar.moreActions')}
                              size="small"
                              color="inherit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDropdown(e);
                                setTargetCalendarForDropdown({
                                  type: normalizedCalendarType as CalendarType,
                                  rootEmail,
                                  calendarIds: calendars.map(({ calendarId }) => calendarId),
                                });
                              }}
                            >
                              <MoreVert className="calendars-list__accordion__title__more" />
                            </IconButton>
                          )}
                        {hasError && (
                          <CustomTooltip
                            title={
                              <SyncErrorTooltipContent
                                disabled={!calendars[0]?.calendarType}
                                onReconnect={() => onConnectCalendar(calendars[0]?.calendarType)}
                                onDisconnect={() =>
                                  onDisconnectCalendar(
                                    calendars.map(({ calendarId }) => calendarId)
                                  )
                                }
                                email={rootEmail}
                                isDisconnecting={isDisconnectingCalendars}
                                isConnecting={isConnectingCalendars}
                              />
                            }
                          >
                            <Error className="calendars-list__accordion__title__error" />
                          </CustomTooltip>
                        )}
                      </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="calendars-list__item-group">
                        {calendars.map((calendar) => (
                          <CalendarListItem
                            key={calendar.calendarId}
                            calendar={calendar}
                            selected={visibleCalendarIds.includes(calendar.calendarId)}
                            onSelect={(checked: boolean) => onSelectCalendar(calendar, checked)}
                          />
                        ))}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          ))}
        <ManageCalendarsBtn
          disabledCalendarType={disabledCalendarType}
          onConnect={onConnectCalendar}
          syncErrorCalendarType={syncErrorCalendarType}
          syncErrorRootEmail={syncErrorRootEmail}
        />
      </div>
      <CalendarListMoreDropdown
        anchorEl={anchorEl}
        onClose={handleCloseCalendarDropdown}
        canDelete={canDisconnectCalendarForDropdown}
        loadingOption={loadingOption}
        onClickItem={handleMoreDropdownItemClick}
      />
    </div>
  );
};

export default CalendarsList;
